'use server';

/**
 * analyze-inspection.ts — Django AI backend version
 * 
 * Firebase Admin SDK (db, storage) removed.
 * All results are now persisted via Django API: POST /api/ai/inspect/
 * 
 * This file re-exports the flow for backward compatibility
 * but calls the Django backend instead of Firebase Firestore.
 */

import { ai } from '@/ai/genkit';
import { z } from 'genkit';

const AnalyzeInspectionInputSchema = z.object({
  make_id: z.string(),
  model_id: z.string(),
  year: z.number().optional(),
  photo_url: z.string().optional().describe('رابط الصورة (Data URI أو URL عام)'),
  photo_b64: z.string().optional().describe('الصورة بتنسيق base64 (إذا كانت محلية)'),
  symptoms: z.array(z.string()),
  part_id: z.string().optional(),
});

export type AnalyzeInspectionInput = z.infer<typeof AnalyzeInspectionInputSchema>;

const InspectionResultSchema = z.object({
  issue_title_ar: z.string(),
  confidence: z.number(),
  risk_level: z.enum(['low', 'medium', 'high', 'critical']),
  impact_ar: z.string(),
  actions_ar: z.array(z.string()),
});

const AnalyzeInspectionOutputSchema = z.object({
  results: z.array(InspectionResultSchema),
  inspection_id: z.string(),
  detected_labels: z.array(z.string()).describe('الوسوم التقنية من الذكاء الاصطناعي'),
  estimated_repair_range: z.string().optional(),
  workshop_booking_recommended: z.boolean().optional(),
  provider: z.string().optional(),
});

export type AnalyzeInspectionOutput = z.infer<typeof AnalyzeInspectionOutputSchema>;

/**
 * Call Django backend AI endpoint for inspection analysis.
 * Falls back to local Genkit Gemini flow if backend is unavailable.
 */
export async function analyzeInspection(input: AnalyzeInspectionInput): Promise<AnalyzeInspectionOutput> {
  const BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

  try {
    const res = await fetch(`${BASE_URL}/api/ai/inspect/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        make_id: input.make_id,
        model_id: input.model_id,
        year: input.year,
        symptoms: input.symptoms,
        photo_url: input.photo_url || '',
        photo_b64: input.photo_b64 || '',
      }),
      // 10 second timeout for AI calls
      signal: AbortSignal.timeout(10000),
    });

    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    return {
      results: data.results || [],
      inspection_id: data.inspection_id || crypto.randomUUID(),
      detected_labels: data.detected_labels || [],
      estimated_repair_range: data.estimated_repair_range,
      workshop_booking_recommended: data.workshop_booking_recommended,
      provider: data.provider || 'django-backend',
    };
  } catch (err) {
    console.warn('[analyzeInspection] Backend unavailable, using fallback:', err);
    // Return graceful fallback
    return {
      inspection_id: crypto.randomUUID(),
      results: [
        {
          issue_title_ar: 'تعذر الاتصال بنظام التحليل الذكي',
          confidence: 0,
          risk_level: 'low',
          impact_ar: 'الرجاء التحقق من اتصال الإنترنت وإعادة المحاولة.',
          actions_ar: ['تحقق من تشغيل خادم Django على المنفذ 8000'],
        },
      ],
      detected_labels: [],
      provider: 'fallback-offline',
    };
  }
}
